import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import history from "../../utils/history";
import urls from "../../global/constants/UrlConstants";
import YhTable, { YhTableCellParams, YhTableColumn } from "../../ui/YhTable";
import YhInput from "../../ui/YhInput";
import YhButton from "../../ui/YhButton";
import YhSelect from "../../ui/YhSelect";
import YhModal from "../../ui/YhModal";
import CustomLoader from "../../global/components/CustomLoader/CustomLoader";
import {
  OrgNameCell,
  useOrgNameMap,
} from "../../Shared/Organization/OrganizationSelect";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import {
  getWhatsAppBroadcastProgress,
  listWhatsAppBroadcastRecipients,
  rescheduleWhatsAppBroadcast,
  retryAllWhatsAppBroadcast,
  retryFailedWhatsAppBroadcast,
  deleteScheduledWhatsAppBroadcast,
} from "../../services/whatsappMarketingService";
import {
  formatPhoneKeepingCountryCode,
  resolveLeadPhoneForDisplay,
} from "../../Shared/Phone/PhoneWithCountryCode";
import MarketingAccessGate from "./MarketingAccessGate";

type ConfirmAction = "failed" | "all" | "delete" | null;

const recipientTone: Record<string, string> = {
  pending: "bg-[#F9F0F4] text-[#4338CA]",
  processing: "bg-[#FEF3C7] text-[#B45309]",
  sent: "bg-[#DCFCE7] text-[#15803D]",
  failed: "bg-[#FEE2E2] text-[#B91C1C]",
  skipped: "bg-[#F3F4F6] text-[#4B5563]",
};

const WhatsAppBroadcastDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const orgNameMap = useOrgNameMap();
  const [broadcast, setBroadcast] = useState<any>(null);
  const [recipients, setRecipients] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [scheduleLocal, setScheduleLocal] = useState("");
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [historyRow, setHistoryRow] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  const toIstLocalInput = (iso?: string) => {
    if (!iso) return "";
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(new Date(iso));
    const get = (t: string) => parts.find((p) => p.type === t)?.value || "";
    return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get(
      "minute"
    )}`;
  };

  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => window.clearTimeout(t);
  }, [search]);

  const loadProgress = useCallback(async () => {
    if (!id) return;
    try {
      const res = await getWhatsAppBroadcastProgress(id);
      if (res?.success === 1) {
        setBroadcast(res.data);
        if (res.data?.status === "scheduled" && res.data?.scheduledAt) {
          setScheduleLocal(
            (prev) => prev || toIstLocalInput(res.data.scheduledAt)
          );
        }
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to load broadcast");
    }
  }, [id]);

  const loadRecipients = useCallback(async () => {
    if (!id) return;
    setLoadingRecipients(true);
    try {
      const res = await listWhatsAppBroadcastRecipients(id, {
        page,
        pageSize,
        status: statusFilter,
        search: searchDebounced,
      });
      setRecipients(Array.isArray(res?.data) ? res.data : []);
      setMeta(res?.metadata?.[0] || {});
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to load report");
    } finally {
      setLoadingRecipients(false);
    }
  }, [id, page, pageSize, statusFilter, searchDebounced]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  useEffect(() => {
    loadRecipients();
  }, [loadRecipients]);

  useEffect(() => {
    if (!broadcast) return;
    if (!["queued", "processing", "scheduled"].includes(broadcast.status))
      return;
    const t = window.setInterval(
      () => {
        loadProgress();
        loadRecipients();
      },
      broadcast.status === "scheduled" ? 15000 : 2500
    );
    return () => window.clearInterval(t);
  }, [broadcast?.status, loadProgress, loadRecipients]);

  const totals = broadcast?.totals || {};
  const percent = Number(broadcast?.progress?.percent || 0);
  const isRunning = ["queued", "processing"].includes(broadcast?.status);
  const isScheduled = broadcast?.status === "scheduled";
  const canRetry =
    !!broadcast &&
    !isRunning &&
    !isScheduled &&
    ((totals.failed || 0) > 0 || (totals.total || 0) > 0);
  const runHistory = useMemo(() => {
    const list = Array.isArray(broadcast?.runHistory) ? broadcast.runHistory : [];
    return [...list].reverse();
  }, [broadcast?.runHistory]);

  const applyStatusFilter = (value: string) => {
    setStatusFilter(value);
    setPage(0);
  };

  const saveSchedule = async (opts?: { runNow?: boolean }) => {
    if (!id) return;
    if (!opts?.runNow && !scheduleLocal.trim()) {
      openErrorNotification("Pick a date and time in IST");
      return;
    }
    setSavingSchedule(true);
    try {
      const res = await rescheduleWhatsAppBroadcast(id, {
        runNow: !!opts?.runNow,
        scheduledAt: opts?.runNow ? undefined : scheduleLocal,
        timezone: "Asia/Kolkata",
      });
      if (!res?.success) {
        openErrorNotification(res?.message || "Could not update schedule");
        return;
      }
      openSuccessNotification(res.message || "Schedule updated");
      await loadProgress();
    } catch (e: any) {
      openErrorNotification(e?.message || "Could not update schedule");
    } finally {
      setSavingSchedule(false);
    }
  };

  const runRequeue = async (scope: "failed" | "all") => {
    if (!id) return;
    setRetrying(true);
    try {
      const res =
        scope === "all"
          ? await retryAllWhatsAppBroadcast(id)
          : await retryFailedWhatsAppBroadcast(id);
      if (!res?.success) {
        openErrorNotification(res?.message || "Could not re-queue broadcast");
        return;
      }
      openSuccessNotification(
        res.message ||
          (scope === "all"
            ? "Full broadcast re-queued"
            : "Failed recipients re-queued")
      );
      setConfirmAction(null);
      applyStatusFilter(scope === "failed" ? "pending" : "all");
      await loadProgress();
      await loadRecipients();
    } catch (e: any) {
      openErrorNotification(e?.message || "Could not re-queue broadcast");
    } finally {
      setRetrying(false);
    }
  };

  const runDeleteSchedule = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      const res = await deleteScheduledWhatsAppBroadcast(id);
      if (!res?.success) {
        openErrorNotification(res?.message || "Could not delete schedule");
        return;
      }
      openSuccessNotification(
        res.message || "Scheduled broadcast deleted — messages will not send"
      );
      setConfirmAction(null);
      history.push(urls.whatsappMarketingPath);
    } catch (e: any) {
      openErrorNotification(e?.message || "Could not delete schedule");
    } finally {
      setDeleting(false);
    }
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "Lead",
      field: "leadName",
      minWidth: 180,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold text-[#111827]">
            {params.row.leadName || "—"}
          </span>
          <span className="truncate text-xs text-[#6B7280]">
            {(() => {
              const phone = formatPhoneKeepingCountryCode(
                resolveLeadPhoneForDisplay(params.row)
              );
              return phone !== "—" ? phone : params.row.leadEmail || "—";
            })()}
          </span>
          {params.row.recipientType === "external" || !params.row.leadId ? (
            <span className="mt-0.5 text-[10px] font-medium text-[#6B7280]">
              File contact (not a CRM lead)
            </span>
          ) : null}
        </div>
      ),
    },
    {
      headerName: "Delivery",
      field: "status",
      minWidth: 120,
      renderCell: (params: YhTableCellParams) => {
        const st = String(params.row.status || "");
        const label =
          st === "sent"
            ? "Sent (provider accepted)"
            : st === "failed"
              ? "Failed / not sent"
              : st === "pending"
                ? "Pending"
                : st === "processing"
                  ? "Sending…"
                  : st === "skipped"
                    ? "Skipped"
                    : st;
        return (
          <span
            className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${
              recipientTone[st] || "bg-[#F3F4F6] text-[#4B5563]"
            }`}
          >
            {label}
          </span>
        );
      },
    },
    {
      headerName: "Attempts",
      field: "attempts",
      minWidth: 110,
      renderCell: (params: YhTableCellParams) => {
        const histLen = Array.isArray(params.row.attemptHistory)
          ? params.row.attemptHistory.length
          : 0;
        return (
          <button
            type="button"
            className="text-left text-[13px] font-semibold text-[#A02448] hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              setHistoryRow(params.row);
            }}
            title="View attempt history"
          >
            {params.row.attempts ?? 0}
            {histLen ? (
              <span className="ml-1 text-[11px] font-normal text-[#6B7280]">
                · {histLen} log{histLen === 1 ? "" : "s"}
              </span>
            ) : null}
          </button>
        );
      },
    },
    {
      headerName: "Error",
      field: "lastError",
      minWidth: 200,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <span className="truncate text-[12px] text-[#B91C1C]">
          {params.row.lastError || "—"}
        </span>
      ),
    },
    {
      headerName: "Sent at",
      field: "sentAt",
      minWidth: 150,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-[13px] text-[#374151]">
          {params.row.sentAt
            ? new Date(params.row.sentAt).toLocaleString()
            : params.row.lastSentAt
              ? `was ${new Date(params.row.lastSentAt).toLocaleString()}`
              : "—"}
        </span>
      ),
    },
  ];

  if (!broadcast) {
    return (
      <MarketingAccessGate>
        <div className="p-6">
          <CustomLoader apiResponse={false} />
        </div>
      </MarketingAccessGate>
    );
  }

  const chipClass = (active: boolean) =>
    `inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition ${
      active
        ? "bg-[#A02448] text-white"
        : "bg-white text-[#374151] ring-1 ring-[#E5E7EB] hover:bg-[#F8FAFC]"
    }`;

  return (
    <MarketingAccessGate>
    <div className="flex h-full min-h-0 flex-col gap-4 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            className="mb-1 text-sm font-semibold text-[#A02448]"
            onClick={() => history.push(urls.whatsappMarketingPath)}
          >
            ← Back to broadcasts
          </button>
          <h1 className="text-xl font-semibold text-[#111827]">
            {broadcast.name}
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            {broadcast.templateName || broadcast.templateWid} ·{" "}
            <span className="capitalize">{broadcast.status}</span>
            {broadcast.progress?.message
              ? ` · ${broadcast.progress.message}`
              : ""}
          </p>
        </div>
        {canRetry ? (
          <div className="flex flex-col items-end gap-2">
            <div className="flex flex-wrap justify-end gap-2">
              {(totals.failed || 0) > 0 ? (
                <YhButton
                  variant="secondary"
                  size="sm"
                  disabled={retrying}
                  onClick={() => {
                    applyStatusFilter("failed");
                    setConfirmAction("failed");
                  }}
                >
                  Retry failed only ({totals.failed})
                </YhButton>
              ) : null}
              <YhButton
                variant="primary"
                size="sm"
                disabled={retrying}
                onClick={() => setConfirmAction("all")}
              >
                Re-run full broadcast
              </YhButton>
            </div>
            <p className="max-w-[300px] text-right text-[11px] text-[#6B7280]">
              Retry failed = only not delivered. Full re-run = everyone again.
              Prior attempt history is kept either way.
            </p>
          </div>
        ) : null}
      </div>

      {isScheduled ? (
        <div className="rounded-2xl border border-[#F9F0F4] bg-[#F8FAFF] p-4">
          <h2 className="text-sm font-semibold text-[#111827]">
            Scheduled send (IST)
          </h2>
          <p className="mt-1 text-xs text-[#6B7280]">
            Change the India Standard Time below, then save. Timezone is always
            Asia/Kolkata.
          </p>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="min-w-[220px]">
              <label className="yh-label mb-1 block">Date &amp; time (IST)</label>
              <input
                type="datetime-local"
                value={scheduleLocal}
                onChange={(e) => setScheduleLocal(e.target.value)}
                className="yh-input w-full rounded-xl border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#111827]"
              />
            </div>
            <YhButton
              variant="primary"
              size="sm"
              disabled={savingSchedule || !scheduleLocal}
              onClick={() => saveSchedule()}
            >
              {savingSchedule ? "Saving…" : "Update to this time"}
            </YhButton>
            <YhButton
              variant="secondary"
              size="sm"
              disabled={savingSchedule}
              onClick={() => saveSchedule({ runNow: true })}
            >
              Send now instead
            </YhButton>
            <YhButton
              variant="secondary"
              size="sm"
              disabled={deleting || savingSchedule}
              onClick={() => setConfirmAction("delete")}
              className="!border-[#FECACA] !text-[#B91C1C] hover:!bg-[#FEF2F2]"
            >
              Delete schedule
            </YhButton>
          </div>
          <p className="mt-2 text-[11px] text-[#6B7280]">
            Delete removes this schedule and queued recipients so WhatsApp will
            not send. Completed / already-sent broadcasts cannot be deleted.
          </p>
        </div>
      ) : null}

      <YhModal
        open={confirmAction === "delete"}
        onClose={() => {
          if (!deleting) setConfirmAction(null);
        }}
        title="Delete scheduled broadcast?"
        width={480}
        footer={
          <>
            <YhButton
              variant="secondary"
              size="sm"
              disabled={deleting}
              onClick={() => setConfirmAction(null)}
            >
              Cancel
            </YhButton>
            <YhButton
              variant="primary"
              size="sm"
              disabled={deleting}
              onClick={() => runDeleteSchedule()}
              className="!bg-[#DC2626] hover:!bg-[#B91C1C]"
            >
              {deleting ? "Deleting…" : "Yes, delete schedule"}
            </YhButton>
          </>
        }
      >
        <p className="text-sm text-[#374151]">
          Delete <strong>{broadcast.name}</strong> and cancel the scheduled
          send?
        </p>
        <p className="mt-2 text-xs text-[#B91C1C]">
          Messages will not be triggered. This cannot be undone.
        </p>
      </YhModal>

      <YhModal
        open={confirmAction === "failed"}
        onClose={() => {
          if (!retrying) setConfirmAction(null);
        }}
        title="Retry failed recipients?"
        width={480}
        footer={
          <>
            <YhButton
              variant="secondary"
              size="sm"
              disabled={retrying}
              onClick={() => setConfirmAction(null)}
            >
              Cancel
            </YhButton>
            <YhButton
              variant="primary"
              size="sm"
              disabled={retrying}
              onClick={() => runRequeue("failed")}
            >
              {retrying ? "Queuing…" : "Yes, retry failed only"}
            </YhButton>
          </>
        }
      >
        <p className="text-sm text-[#374151]">
          Re-run only the <strong>{totals.failed || 0}</strong> failed recipient
          {(totals.failed || 0) === 1 ? "" : "s"}?
        </p>
        <p className="mt-2 text-xs text-[#6B7280]">
          Delivered messages will not be sent again. Previous attempt errors and
          send times stay in history.
        </p>
      </YhModal>

      <YhModal
        open={confirmAction === "all"}
        onClose={() => {
          if (!retrying) setConfirmAction(null);
        }}
        title="Re-run full broadcast?"
        width={480}
        footer={
          <>
            <YhButton
              variant="secondary"
              size="sm"
              disabled={retrying}
              onClick={() => setConfirmAction(null)}
            >
              Cancel
            </YhButton>
            <YhButton
              variant="primary"
              size="sm"
              disabled={retrying}
              onClick={() => runRequeue("all")}
            >
              {retrying ? "Queuing…" : "Yes, re-run all"}
            </YhButton>
          </>
        }
      >
        <p className="text-sm text-[#374151]">
          Re-run the <strong>full broadcast queue</strong> ({totals.total || 0}{" "}
          recipient
          {(totals.total || 0) === 1 ? "" : "s"})?
        </p>
        <p className="mt-2 text-xs text-[#B45309]">
          This queues everyone again, including recipients who already received
          the message. Prior attempt history is still kept.
        </p>
      </YhModal>

      <YhModal
        open={!!historyRow}
        onClose={() => setHistoryRow(null)}
        title={`Attempt history — ${historyRow?.leadName || "Lead"}`}
        width={560}
        footer={
          <YhButton
            variant="secondary"
            size="sm"
            onClick={() => setHistoryRow(null)}
          >
            Close
          </YhButton>
        }
      >
        {historyRow ? (
          <div className="space-y-3">
            <p className="text-xs text-[#6B7280]">
              Current status:{" "}
              <strong className="capitalize text-[#111827]">
                {historyRow.status}
              </strong>{" "}
              · attempts {historyRow.attempts ?? 0}
              {historyRow.requeueCount
                ? ` · re-queued ${historyRow.requeueCount}×`
                : ""}
            </p>
            {Array.isArray(historyRow.attemptHistory) &&
            historyRow.attemptHistory.length ? (
              <ul className="max-h-[360px] space-y-2 overflow-y-auto">
                {[...historyRow.attemptHistory]
                  .reverse()
                  .map((h: any, i: number) => (
                    <li
                      key={`${h.at || i}-${i}`}
                      className="rounded-xl border border-[#EEF0F5] bg-[#F8FAFC] px-3 py-2"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                            recipientTone[h.status] ||
                            "bg-[#F3F4F6] text-[#4B5563]"
                          }`}
                        >
                          {h.event || "event"} · {h.status || "—"}
                        </span>
                        <span className="text-[11px] text-[#6B7280]">
                          {h.at ? new Date(h.at).toLocaleString() : "—"}
                        </span>
                      </div>
                      {h.error ? (
                        <p className="mt-1 text-[12px] text-[#B91C1C]">
                          {h.error}
                        </p>
                      ) : null}
                      {h.runLabel ? (
                        <p className="mt-1 text-[11px] text-[#6B7280]">
                          {h.runLabel}
                        </p>
                      ) : null}
                    </li>
                  ))}
              </ul>
            ) : (
              <p className="text-sm text-[#6B7280]">
                No attempt history yet for this recipient.
              </p>
            )}
          </div>
        ) : null}
      </YhModal>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-[#EEF0F5] bg-white p-4">
          <p className="text-xs font-semibold uppercase text-[#6B7280]">
            Organization
          </p>
          <div className="mt-1">
            <OrgNameCell
              organizationId={broadcast.organizationId}
              map={orgNameMap}
            />
          </div>
        </div>
        <div className="rounded-xl border border-[#EEF0F5] bg-white p-4">
          <p className="text-xs font-semibold uppercase text-[#6B7280]">
            Project
          </p>
          <p className="mt-1 text-sm font-semibold text-[#111827]">
            {broadcast.projectName ||
              (broadcast.projectId ? String(broadcast.projectId) : "All")}
          </p>
        </div>
        <button
          type="button"
          className={`rounded-xl border p-4 text-left transition ${
            statusFilter === "sent"
              ? "border-[#86EFAC] bg-[#F0FDF4]"
              : "border-[#EEF0F5] bg-white hover:border-[#BBF7D0]"
          }`}
          onClick={() =>
            applyStatusFilter(statusFilter === "sent" ? "all" : "sent")
          }
        >
          <p className="text-xs font-semibold uppercase text-[#6B7280]">
            Delivered
          </p>
          <p className="mt-1 text-sm font-semibold text-[#15803D]">
            {totals.sent || 0} / {totals.total || 0}
          </p>
          <p className="mt-1 text-[11px] text-[#6B7280]">Click to filter</p>
        </button>
        <button
          type="button"
          className={`rounded-xl border p-4 text-left transition ${
            statusFilter === "failed"
              ? "border-[#FCA5A5] bg-[#FEF2F2]"
              : "border-[#EEF0F5] bg-white hover:border-[#FECACA]"
          }`}
          onClick={() =>
            applyStatusFilter(statusFilter === "failed" ? "all" : "failed")
          }
        >
          <p className="text-xs font-semibold uppercase text-[#6B7280]">
            Failed (not delivered)
          </p>
          <p className="mt-1 text-sm font-semibold text-[#B91C1C]">
            {totals.failed || 0}
            {(totals.skipped || 0) > 0 ? (
              <span className="ml-1 text-xs font-normal text-[#6B7280]">
                · {totals.skipped} skipped
              </span>
            ) : null}
          </p>
          <p className="mt-1 text-[11px] text-[#6B7280]">
            Click to filter failed only
          </p>
        </button>
      </div>

      <div className="rounded-xl border border-[#EEF0F5] bg-white p-4">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-[#111827]">Progress</p>
          <span className="text-sm font-semibold text-[#A02448]">
            {percent}%
          </span>
        </div>
        <div className="h-3 w-full overflow-hidden rounded-full bg-[#E5E7EB]">
          <div
            className="h-full rounded-full bg-[#A02448] transition-all duration-500"
            style={{ width: `${Math.min(100, percent)}%` }}
          />
        </div>
        <p className="mt-2 text-xs text-[#6B7280]">
          Processing continues in the background — you can leave this page and
          return anytime for the report.
        </p>
      </div>

      {runHistory.length ? (
        <div className="rounded-xl border border-[#EEF0F5] bg-white p-4">
          <h2 className="text-sm font-semibold text-[#111827]">Run history</h2>
          <p className="mt-0.5 text-xs text-[#6B7280]">
            Initial send and each retry / full re-run (kept after re-runs).
          </p>
          <ul className="mt-3 max-h-48 space-y-2 overflow-y-auto">
            {runHistory.map((h: any, i: number) => (
              <li
                key={`${h.at || i}-${i}`}
                className="flex flex-wrap items-start justify-between gap-2 rounded-lg bg-[#F8FAFC] px-3 py-2 text-xs"
              >
                <div>
                  <p className="font-semibold capitalize text-[#111827]">
                    {h.event || "run"}
                    {h.scope ? ` · ${h.scope}` : ""}
                    {h.status ? ` · ${h.status}` : ""}
                  </p>
                  <p className="mt-0.5 text-[#6B7280]">{h.message || "—"}</p>
                  {h.totals || h.totalsBefore ? (
                    <p className="mt-0.5 text-[#6B7280]">
                      {h.requeued != null ? `Re-queued ${h.requeued} · ` : ""}
                      sent {(h.totals || h.totalsBefore)?.sent ?? "—"} / failed{" "}
                      {(h.totals || h.totalsBefore)?.failed ?? "—"}
                    </p>
                  ) : null}
                </div>
                <span className="shrink-0 text-[#9CA3AF]">
                  {h.at ? new Date(h.at).toLocaleString() : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className={chipClass(statusFilter === "all")}
            onClick={() => applyStatusFilter("all")}
          >
            All ({totals.total || 0})
          </button>
          <button
            type="button"
            className={chipClass(statusFilter === "sent")}
            onClick={() => applyStatusFilter("sent")}
          >
            Delivered ({totals.sent || 0})
          </button>
          <button
            type="button"
            className={chipClass(statusFilter === "failed")}
            onClick={() => applyStatusFilter("failed")}
          >
            Failed ({totals.failed || 0})
          </button>
          <button
            type="button"
            className={chipClass(statusFilter === "pending")}
            onClick={() => applyStatusFilter("pending")}
          >
            Pending ({totals.pending || 0})
          </button>
          <button
            type="button"
            className={chipClass(statusFilter === "skipped")}
            onClick={() => applyStatusFilter("skipped")}
          >
            Skipped ({totals.skipped || 0})
          </button>
        </div>
        <div className="min-w-[180px]">
          <YhSelect
            label="Delivery status"
            value={statusFilter}
            onChange={(e: any) => applyStatusFilter(e.target.value || "all")}
            options={[
              { label: "All", value: "all" },
              { label: "Delivered / sent", value: "sent" },
              { label: "Not delivered (failed)", value: "failed" },
              { label: "Pending", value: "pending" },
              { label: "Skipped", value: "skipped" },
            ]}
          />
        </div>
        <div className="min-w-[200px] flex-1">
          <YhInput
            label="Search recipients"
            value={search}
            onChange={(e: any) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Name, phone, email"
          />
        </div>
        {statusFilter === "failed" && (totals.failed || 0) > 0 && canRetry ? (
          <YhButton
            variant="secondary"
            size="sm"
            disabled={retrying}
            onClick={() => setConfirmAction("failed")}
          >
            Retry these failed ({totals.failed})
          </YhButton>
        ) : null}
      </div>

      <div className="relative min-h-0 flex-1">
        <YhTable
          columns={columns}
          rows={recipients}
          getRowId={(row) => String(row._id)}
          loading={loadingRecipients}
          emptyMessage={
            statusFilter === "failed"
              ? "No failed recipients in this filter"
              : "No recipients in this report page"
          }
          className="h-full min-h-[280px]"
          page={page}
          pageSize={pageSize}
          totalRows={meta?.total || 0}
          paginationLabel="recipients"
          onPageChange={setPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(0);
          }}
        />
        {loadingRecipients ? <CustomLoader apiResponse={false} /> : null}
      </div>
    </div>
    </MarketingAccessGate>
  );
};

export default WhatsAppBroadcastDetailPage;
